import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  FileText, Plus, Trash2, Upload, Download, ArrowLeft, Save,
  CheckCircle, AlertCircle, Play, Code2, BarChart3, PenLine,
  BookOpen, Wrench, Scale, ChevronRight, X, Edit2, Search, ChevronDown
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

// ─── Constants ───────────────────────────────────────────────────────────────
const QUOTIENTS = ['CRQ', 'SRQ', 'LQ', 'SIQ', 'PEQ', 'DAQ', 'SEQ'];
const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];
const CODE_LANGUAGES = ['Python', 'JavaScript', 'Java', 'C++', 'C', 'SQL', 'R', 'TypeScript', 'Go', 'Rust'];
const TOOL_OPTIONS = ['Tally', 'AutoCAD', 'Figma', 'SAP', 'MS Excel', 'Power BI', 'Tableau', 'Other'];

// ─── Engine Type Definitions ──────────────────────────────────────────────────
const ENGINE_TYPES = [
  {
    id: 'KNOW',
    label: 'KNOW',
    title: 'Knowledge Check',
    desc: 'Concepts, regulations, theory — Auto MCQ + short answer',
    icon: BookOpen,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-500 dark:border-blue-400',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    ai: 'Light'
  },
  {
    id: 'CODE',
    label: 'CODE',
    title: 'Programming',
    desc: 'Programming, SQL, scripting — Judge0 runs code against test cases',
    icon: Code2,
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-500 dark:border-emerald-400',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    ai: 'No'
  },
  {
    id: 'DATA',
    label: 'DATA',
    title: 'Data & Analysis',
    desc: 'Excel, analysis, numeric problems — Auto-check + Claude vision',
    icon: BarChart3,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-500 dark:border-violet-400',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    ai: 'Some'
  },
  {
    id: 'WRITE',
    label: 'WRITE',
    title: 'Written Response',
    desc: 'Business writing, case analysis — Claude scores against a rubric',
    icon: PenLine,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-500 dark:border-amber-400',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    ai: 'Yes'
  },
  {
    id: 'TOOL',
    label: 'TOOL',
    title: 'Tool Proficiency',
    desc: 'Tally, AutoCAD, Figma, SAP — Upload screenshot; Claude vision',
    icon: Wrench,
    color: 'cyan',
    gradient: 'from-cyan-500 to-sky-600',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    border: 'border-cyan-500 dark:border-cyan-400',
    badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
    ai: 'Some'
  },
  {
    id: 'JUDGE',
    label: 'JUDGE',
    title: 'Judgment & Ethics',
    desc: 'Applied judgment, ethics, decisions — Scenario ranking; auto concordance',
    icon: Scale,
    color: 'rose',
    gradient: 'from-rose-500 to-pink-600',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-500 dark:border-rose-400',
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rosese-300',
    ai: 'No'
  },
];

// ─── AI Badge ─────────────────────────────────────────────────────────────────
const AIBadge = ({ level }) => {
  const map = {
    'No': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'Light': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    'Some': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    'Yes': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  return (
    <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full ${map[level] || map['No']}`}>
      AI: {level}
    </span>
  );
};

// ─── Empty question form per engine type ──────────────────────────────────────
const emptyQuestion = (engineType = 'KNOW') => ({
  questionId: '',
  questionText: '',
  quotient: 'CRQ',
  difficultyLevel: 'Easy',
  engineType,
  // KNOW / JUDGE
  optionA: '', optionB: '', optionC: '', optionD: '',
  correctAnswer: 'A',
  concordanceKey: '', // JUDGE only
  // CODE
  language: 'Python',
  starterCode: '',
  expectedOutput: '',
  // WRITE
  rubric: '',
  wordLimit: '',
  // DATA
  expectedAnswer: '',
  tolerance: '5',
  // TOOL
  toolName: 'Tally',
  toolInstructions: '',
});

// ─── Main Component ───────────────────────────────────────────────────────────
const CreateAssessment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [assessmentType, setAssessmentType] = useState('course');
  const [skills, setSkills] = useState([]);

  const initialFormState = {
    assessmentName: '',
    description: '',
    instructions: '',
    assessmentEngineType: 'KNOW',
    totalQuestions: '',
    duration: '60',
    maxAttempts: '1',
    questionsPerAttempt: '',
    passingScore: '70',
    randomizeQuestions: false,
    questions: []
  };

  const [courseFormData, setCourseFormData] = useState({ ...initialFormState, assessmentEngineType: 'KNOW' });
  const [skillFormData, setSkillFormData] = useState({ ...initialFormState, assessmentEngineType: '' });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    assessmentName: '',
    description: '',
    instructions: '',
    assessmentEngineType: 'KNOW',
    totalQuestions: '',
    duration: '60',
    maxAttempts: '1',
    questionsPerAttempt: '',
    passingScore: '70',
    randomizeQuestions: false,
    questions: []
  });

  const [questionForm, setQuestionForm] = useState(emptyQuestion('KNOW'));
  const [editingIndex, setEditingIndex] = useState(-1);

  useEffect(() => {
    if (!id && location.state?.type === 'skill') {
      setAssessmentType('skill');
      setFormData({
        assessmentName: '',
        description: '',
        instructions: '',
        assessmentEngineType: '',
        totalQuestions: '',
        duration: '60',
        maxAttempts: '1',
        questionsPerAttempt: '',
        passingScore: '70',
        randomizeQuestions: false,
        questions: []
      });
      setQuestionForm(emptyQuestion(''));
    }
  }, [location.state, id]);

  // Derived
  const selectedEngine = ENGINE_TYPES.find(e => e.id === formData.assessmentEngineType);
  const dropdownOptions = assessmentType === 'skill'
    ? skills.map(s => s.name).filter(Boolean)
    : ['Baseline', 'Capacity', 'Capability', 'Leadership', 'AIQ', 'SQ', 'PIQ'];
  const filteredDropdownOptions = dropdownOptions.filter(opt =>
    (opt || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── Fetch skills on mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const { data } = await api.get('/skillslist');
        if (data.success && Array.isArray(data.data)) {
          setSkills(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch skills:', err);
      }
    };
    fetchSkills();
  }, []);

  // ── Fetch existing assessment
  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchAssessment();
    }
  }, [id]);

  const fetchAssessment = async () => {
    try {
      let response;
      let isSkill = false;
      try {
        response = await api.get(`/assessments/${id}`);
      } catch (err) {
        response = await api.get(`/skill-assessments/${id}`);
        isSkill = true;
      }
      const { data } = response;
      if (data.success) {
        const a = data.data;
        const loadedForm = {
          assessmentName: a.assessmentName,
          description: a.description,
          instructions: a.instructions || a.questionCategory || '',
          assessmentEngineType: a.assessmentEngineType || 'KNOW',
          totalQuestions: a.totalQuestions !== undefined ? a.totalQuestions : (a.questions?.length || ''),
          duration: a.duration !== undefined ? a.duration : '60',
          maxAttempts: a.maxAttempts !== undefined ? a.maxAttempts : '1',
          questionsPerAttempt: a.questionsPerAttempt !== undefined ? a.questionsPerAttempt : '',
          passingScore: a.passingScore !== undefined ? a.passingScore : '70',
          randomizeQuestions: !!a.randomizeQuestions,
          questions: a.questions.map(q => ({
            questionId: q.questionId || `Q${Math.random().toString(36).substr(2, 5)}`,
            questionText: q.questionText,
            quotient: q.quotient || 'CRQ',
            difficultyLevel: q.difficultyLevel || 'Easy',
            engineType: q.engineType || a.assessmentEngineType || 'KNOW',
            optionA: q.options?.[0]?.label || q.optionA || '',
            optionB: q.options?.[1]?.label || q.optionB || '',
            optionC: q.options?.[2]?.label || q.optionC || '',
            optionD: q.options?.[3]?.label || q.optionD || '',
            correctAnswer: q.correctAnswer || 'A',
            concordanceKey: q.concordanceKey || '',
            language: q.language || 'Python',
            starterCode: q.starterCode || '',
            expectedOutput: q.expectedOutput || '',
            rubric: q.rubric || '',
            wordLimit: q.wordLimit || '',
            expectedAnswer: q.expectedAnswer || '',
            tolerance: q.tolerance || '5',
            toolName: q.toolName || 'Tally',
            toolInstructions: q.toolInstructions || '',
          }))
        };
        setFormData(loadedForm);
        const type = isSkill ? 'skill' : 'course';
        setAssessmentType(type);
        if (isSkill) {
          setSkillFormData(loadedForm);
        } else {
          setCourseFormData(loadedForm);
        }
      }
    } catch {
      toast.error('Failed to load assessment');
    }
  };

  // ── Step 1 Handlers
  const handleBasicInfoChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectEngineType = (typeId) => {
    setFormData(prev => ({ ...prev, assessmentEngineType: typeId }));
    setQuestionForm(emptyQuestion(typeId));
  };

  const saveBasicInfo = () => {
    if (!formData.assessmentName.trim()) return toast.error('Assessment Name is required');
    if (!formData.assessmentEngineType) return toast.error('Please select an Assessment Engine Type');
    if (!formData.instructions.trim()) return toast.error(assessmentType === 'skill' ? 'Skill selection is required' : 'Course selection is required');
    if (formData.totalQuestions === '' || isNaN(formData.totalQuestions) || Number(formData.totalQuestions) < 0)
      return toast.error('Please enter a valid number for total questions');
    if (formData.questionsPerAttempt === '' || isNaN(formData.questionsPerAttempt) || Number(formData.questionsPerAttempt) <= 0)
      return toast.error('Please enter a valid number of questions per attempt');
    if (Number(formData.questionsPerAttempt) > Number(formData.totalQuestions))
      return toast.error('Questions per attempt cannot exceed total questions');
    if (formData.duration === '' || isNaN(formData.duration) || Number(formData.duration) <= 0)
      return toast.error('Please enter a valid duration (in minutes)');
    if (formData.maxAttempts === '' || isNaN(formData.maxAttempts) || Number(formData.maxAttempts) <= 0)
      return toast.error('Please enter a valid number of attempts');
    if (formData.passingScore === '' || isNaN(formData.passingScore) || Number(formData.passingScore) < 0 || Number(formData.passingScore) > 100)
      return toast.error('Passing score must be between 0 and 100');
    if (!formData.description.trim()) return toast.error('Description is required');
    setCurrentStep(2);
    toast.success('Basic information saved');
  };

  // ── Step 2 Handlers
  const handleQuestionFormChange = (e) => {
    setQuestionForm({ ...questionForm, [e.target.name]: e.target.value });
  };

  const resetQuestionForm = () => {
    setQuestionForm(emptyQuestion(formData.assessmentEngineType));
    setEditingIndex(-1);
  };

  const saveQuestion = () => {
    if (!questionForm.questionId || !questionForm.questionText) {
      return toast.error('Question ID and Question Text are required');
    }
    const isDuplicateId = formData.questions.some((q, idx) =>
      q.questionId === questionForm.questionId && idx !== editingIndex
    );
    if (isDuplicateId) return toast.error('Question ID must be unique');

    // Type-specific validation
    const t = formData.assessmentEngineType;
    if ((t === 'KNOW' || t === 'JUDGE') && (!questionForm.optionA || !questionForm.optionB || !questionForm.optionC || !questionForm.optionD))
      return toast.error('All four options are required for this type');
    if (t === 'CODE' && !questionForm.expectedOutput.trim())
      return toast.error('Expected output is required for CODE type');
    if (t === 'WRITE' && !questionForm.rubric.trim())
      return toast.error('Rubric is required for WRITE type');
    if (t === 'DATA' && !questionForm.expectedAnswer.trim())
      return toast.error('Expected answer is required for DATA type');

    const newQuestions = [...formData.questions];
    if (editingIndex >= 0) {
      newQuestions[editingIndex] = { ...questionForm, engineType: formData.assessmentEngineType };
      toast.success('Question updated');
    } else {
      newQuestions.push({ ...questionForm, engineType: formData.assessmentEngineType });
      toast.success('Question added');
    }
    setFormData({ ...formData, questions: newQuestions });
    resetQuestionForm();
  };

  const editQuestion = (index) => {
    setQuestionForm(formData.questions[index]);
    setEditingIndex(index);
  };

  const deleteQuestion = (index) => {
    setFormData({ ...formData, questions: formData.questions.filter((_, i) => i !== index) });
    toast.success('Question removed');
  };

  // ── CSV Template Download (type-aware)
  const downloadSampleCSV = () => {
    const t = formData.assessmentEngineType;
    let headers, sampleRows;

    if (t === 'CODE') {
      headers = ['Question ID', 'Question Text', 'Language', 'Starter Code', 'Expected Output', 'Quotient', 'Difficulty'];
      sampleRows = [['Q101', 'Write a function to reverse a string', 'Python', 'def reverse(s):\\n    pass', 'dlrow olleh', 'DAQ', 'Easy']];
    } else if (t === 'WRITE') {
      headers = ['Question ID', 'Question Text', 'Rubric', 'Word Limit', 'Quotient', 'Difficulty'];
      sampleRows = [['Q101', 'Write a business email declining a meeting', 'Clarity,Structure,Professionalism,Conciseness', '150', 'CRQ', 'Medium']];
    } else if (t === 'DATA') {
      headers = ['Question ID', 'Question Text', 'Expected Answer', 'Tolerance %', 'Quotient', 'Difficulty'];
      sampleRows = [['Q101', 'What is the CAGR from 2020 to 2024 given the values below?', '12.5', '2', 'DAQ', 'Hard']];
    } else if (t === 'TOOL') {
      headers = ['Question ID', 'Question Text', 'Tool Name', 'Tool Instructions', 'Quotient', 'Difficulty'];
      sampleRows = [['Q101', 'Create a balance sheet in Tally for FY 2024', 'Tally', 'Use the data provided in the attached sheet', 'SRQ', 'Medium']];
    } else if (t === 'JUDGE') {
      headers = ['Question ID', 'Question Text', 'Option A', 'Option B', 'Option C', 'Option D', 'Concordance Key', 'Quotient', 'Difficulty'];
      sampleRows = [['Q101', 'Your manager asks you to falsify a report. You should...', 'Refuse immediately', 'Ask for reasons first', 'Consult HR', 'Comply to avoid conflict', 'A>C>B>D', 'SEQ', 'Hard']];
    } else {
      // KNOW (default)
      headers = ['Question ID', 'Question Text', 'Option A', 'Option B', 'Option C', 'Option D', 'Correct Answer', 'Quotient', 'Difficulty'];
      sampleRows = [['Q101', 'What is the capital of France?', 'London', 'Berlin', 'Paris', 'Madrid', 'C', 'CRQ', 'Easy']];
    }

    const csvContent = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessment_template_${t || 'KNOW'}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ── CSV/Excel Bulk Upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1 });
      if (!rows || rows.length < 2) return toast.error('File appears empty');

      const headers = rows[0].map(h => String(h).toLowerCase().trim());
      const colMap = {};
      headers.forEach((h, i) => { colMap[h] = i; });
      const getVal = (row, colName) => {
        const idx = colMap[colName];
        return row[idx] !== undefined ? String(row[idx]).trim() : '';
      };

      const t = formData.assessmentEngineType;
      const parsed = [];
      const errors = [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.length === 0) continue;
        const qId = getVal(row, 'question id');
        if (!qId) continue;

        const base = {
          questionId: qId,
          questionText: getVal(row, 'question text'),
          quotient: getVal(row, 'quotient') || 'CRQ',
          difficultyLevel: getVal(row, 'difficulty') || 'Easy',
          engineType: t,
          ...emptyQuestion(t) // defaults
        };

        if (t === 'CODE') {
          parsed.push({ ...base, language: getVal(row, 'language') || 'Python', starterCode: getVal(row, 'starter code'), expectedOutput: getVal(row, 'expected output') });
        } else if (t === 'WRITE') {
          parsed.push({ ...base, rubric: getVal(row, 'rubric'), wordLimit: getVal(row, 'word limit') });
        } else if (t === 'DATA') {
          parsed.push({ ...base, expectedAnswer: getVal(row, 'expected answer'), tolerance: getVal(row, 'tolerance %') || '5' });
        } else if (t === 'TOOL') {
          parsed.push({ ...base, toolName: getVal(row, 'tool name') || 'Tally', toolInstructions: getVal(row, 'tool instructions') });
        } else if (t === 'JUDGE') {
          parsed.push({ ...base, optionA: getVal(row, 'option a'), optionB: getVal(row, 'option b'), optionC: getVal(row, 'option c'), optionD: getVal(row, 'option d'), concordanceKey: getVal(row, 'concordance key') });
        } else {
          const correctAns = getVal(row, 'correct answer').toUpperCase();
          if (!['A', 'B', 'C', 'D'].includes(correctAns)) { errors.push(`Row ${i + 1}: Invalid Correct Answer "${correctAns}"`); continue; }
          parsed.push({ ...base, optionA: getVal(row, 'option a'), optionB: getVal(row, 'option b'), optionC: getVal(row, 'option c'), optionD: getVal(row, 'option d'), correctAnswer: correctAns });
        }
      }

      if (errors.length > 0) return toast.error(`${errors.length} errors. First: ${errors[0]}`);
      setFormData(prev => ({ ...prev, questions: [...prev.questions, ...parsed] }));
      toast.success(`${parsed.length} questions imported`);
    } catch {
      toast.error('Error parsing file');
    }
    e.target.value = '';
  };

  // ── Final Save
  const handleFinalSave = async (status) => {
    if (formData.questions.length === 0) return toast.error('Add at least one question');
    setLoading(true);
    try {
      const backendPayload = {
        assessmentName: formData.assessmentName,
        description: formData.description,
        instructions: formData.instructions,
        questionCategory: formData.instructions,
        assessmentEngineType: formData.assessmentEngineType,
        assessmentType,
        totalQuestions: Number(formData.totalQuestions) || 0,
        maxAttempts: Number(formData.maxAttempts) || 1,
        questionsPerAttempt: formData.questionsPerAttempt !== '' ? Number(formData.questionsPerAttempt) : 0,
        passingScore: Number(formData.passingScore) || 70,
        randomizeQuestions: formData.randomizeQuestions,
        duration: Number(formData.duration) || 60,
        status,
        createdBy: user._id,
        questions: formData.questions.map(q => ({
          questionId: q.questionId,
          questionText: q.questionText,
          type: formData.assessmentEngineType === 'KNOW' ? 'mcq' : formData.assessmentEngineType.toLowerCase(),
          engineType: formData.assessmentEngineType,
          quotient: q.quotient,
          difficultyLevel: q.difficultyLevel,
          // KNOW / JUDGE fields
          options: (formData.assessmentEngineType === 'KNOW' || formData.assessmentEngineType === 'JUDGE')
            ? [{ value: 'A', label: q.optionA }, { value: 'B', label: q.optionB }, { value: 'C', label: q.optionC }, { value: 'D', label: q.optionD }]
            : [],
          correctAnswer: q.correctAnswer,
          concordanceKey: q.concordanceKey,
          // CODE
          language: q.language,
          starterCode: q.starterCode,
          expectedOutput: q.expectedOutput,
          // WRITE
          rubric: q.rubric,
          wordLimit: q.wordLimit,
          // DATA
          expectedAnswer: q.expectedAnswer,
          tolerance: q.tolerance,
          // TOOL
          toolName: q.toolName,
          toolInstructions: q.toolInstructions,
        }))
      };

      const endpoint = assessmentType === 'skill' ? `/skill-assessments` : `/assessments`;

      if (isEditMode) {
        await api.put(`${endpoint}/${id}`, backendPayload);
        toast.success('Assessment updated successfully');
      } else {
        await api.post(endpoint, backendPayload);
        toast.success(status === 'active' ? 'Assessment Activated!' : 'Saved as Draft');
      }
      navigate('/assessments');
    } catch {
      toast.error('Failed to save assessment');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Step 1 — Basic Info + Engine Type Picker
  // ─────────────────────────────────────────────────────────────────────────────
  const renderSettingsAndToggles = () => (
    <>
      {/* Settings grid */}
      <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'Total Questions *', name: 'totalQuestions', type: 'number', placeholder: '0', min: '0' },
            { label: 'Questions Per Attempt *', name: 'questionsPerAttempt', type: 'number', placeholder: '10', min: '1' },
            { label: 'Duration (mins) *', name: 'duration', type: 'number', placeholder: '60', min: '1' },
          ].map(f => (
            <div key={f.name}>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">{f.label}</label>
              <input
                type={f.type} name={f.name} value={formData[f.name]} onChange={handleBasicInfoChange}
                placeholder={f.placeholder} min={f.min} max={f.max}
                className="w-full py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Max Attempts *', name: 'maxAttempts', type: 'number', placeholder: '1', min: '1' },
            { label: 'Passing Score (%) *', name: 'passingScore', type: 'number', placeholder: '70', min: '0', max: '100' },
          ].map(f => (
            <div key={f.name}>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">{f.label}</label>
              <input
                type={f.type} name={f.name} value={formData[f.name]} onChange={handleBasicInfoChange}
                placeholder={f.placeholder} min={f.min} max={f.max}
                className="w-full py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Randomize toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#001020] border border-gray-100 dark:border-gray-800 rounded-xl mt-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#1a3884]/10 dark:bg-[#1a3884]/20 flex items-center justify-center shrink-0">
            <Play className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#00152E] dark:text-white">Randomize Questions</p>
            <p className="text-xs text-gray-500 mt-0.5">Each student sees questions in a different random order</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, randomizeQuestions: !prev.randomizeQuestions }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${formData.randomizeQuestions ? 'bg-[#1a3884]' : 'bg-gray-200 dark:bg-gray-700'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${formData.randomizeQuestions ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={saveBasicInfo}
          className="bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#4f7dff] text-white px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition-all shadow-lg shadow-[#1a3884]/20"
        >
          Next: Add Questions <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );

  const renderCourseForm = () => (
    <div className="bg-white dark:bg-[#001736] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 space-y-5">
      <h2 className="text-base font-extrabold text-[#00152E] dark:text-white border-b border-gray-100 dark:border-gray-800 pb-3">Course Assessment Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Assessment Name *</label>
          <input
            name="assessmentName" value={formData.assessmentName} onChange={handleBasicInfoChange}
            placeholder="e.g. name of assessment"
            className="w-full py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
          />
        </div>
        <div className="relative" ref={dropdownRef}>
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Course / Module *</label>
          <button
            type="button"
            onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
              setSearchQuery('');
            }}
            className="w-full flex items-center justify-between py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all text-left"
          >
            <span>{formData.instructions || 'Select Course'}</span>
            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
          </button>

          {isDropdownOpen && (
            <div className="absolute z-50 w-full mt-2 bg-white dark:bg-[#001736] border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 bg-gray-50/50 dark:bg-[#001020]/50">
                <Search className="w-4 h-4 text-gray-400 shrink-0 ml-1" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 py-1"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="max-h-60 overflow-y-auto py-1">
                {filteredDropdownOptions.length > 0 ? (
                  filteredDropdownOptions.map((opt) => {
                    const isSelected = formData.instructions === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, instructions: opt });
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left py-2 px-4 text-sm hover:bg-[#1a3884]/10 dark:hover:bg-[#1a3884]/20 transition-all flex items-center justify-between
                          ${isSelected ? 'bg-[#1a3884]/5 dark:bg-[#1a3884]/15 font-bold text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-700 dark:text-gray-300'}`}
                      >
                        <span>{opt}</span>
                        {isSelected && <CheckCircle className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />}
                      </button>
                    );
                  })
                ) : (
                  <div className="py-3 px-4 text-xs text-gray-400 text-center">
                    No matches found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Description *</label>
          <textarea
            name="description" value={formData.description} onChange={handleBasicInfoChange} rows={3}
            placeholder="Describe the purpose and scope of this assessment..."
            className="w-full py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none"
          />
        </div>
      </div>

      {renderSettingsAndToggles()}
    </div>
  );

  const renderSkillForm = () => (
    <div className="space-y-6">
      {/* Engine Type Picker */}
      <div className="bg-white dark:bg-[#001736] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
        <div>
          <h2 className="text-base font-extrabold text-[#00152E] dark:text-white">Assessment Engine Type *</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Select the verification engine for all questions in this assessment</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {ENGINE_TYPES.map(eng => {
            const Icon = eng.icon;
            const isSelected = formData.assessmentEngineType === eng.id;
            return (
              <button
                key={eng.id}
                type="button"
                onClick={() => selectEngineType(eng.id)}
                className={`relative text-left p-4 rounded-2xl border-2 transition-all duration-200 group overflow-hidden
                  ${isSelected
                    ? `${eng.border} ${eng.bg} shadow-lg scale-[1.02]`
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/30 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                  }`}
              >
                {isSelected && (
                  <div className={`absolute inset-0 bg-gradient-to-br ${eng.gradient} opacity-5 pointer-events-none`} />
                )}
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-2">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isSelected ? `bg-gradient-to-br ${eng.gradient} text-white shadow-md` : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    {isSelected
                      ? <CheckCircle className="w-4 h-4 text-current opacity-80" style={{ color: `var(--${eng.color}-500)` }} />
                      : <span className="w-4 h-4 rounded-full border-2 border-gray-200 dark:border-gray-600 group-hover:border-gray-300" />
                    }
                  </div>
                  <p className={`text-sm font-extrabold tracking-wide ${isSelected ? '' : 'text-gray-800 dark:text-gray-200'}`}>
                    {eng.label}
                  </p>
                  <p className={`text-[10px] font-semibold mt-0.5 ${isSelected ? 'text-current opacity-70' : 'text-gray-400 dark:text-gray-500'}`}>
                    {eng.title}
                  </p>
                  <p className={`text-[9px] mt-2 leading-snug ${isSelected ? 'text-current opacity-60' : 'text-gray-400 dark:text-gray-500'}`}>
                    {eng.desc}
                  </p>
                  <div className="mt-2">
                    <AIBadge level={eng.ai} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white dark:bg-[#001736] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 space-y-5">
        <h2 className="text-base font-extrabold text-[#00152E] dark:text-white border-b border-gray-100 dark:border-gray-800 pb-3">Skill Assessment Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Assessment Name *</label>
            <input
              name="assessmentName" value={formData.assessmentName} onChange={handleBasicInfoChange}
              placeholder="e.g. name of assessment"
              className="w-full py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
            />
          </div>
          <div className="relative" ref={dropdownRef}>
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Skill *</label>
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(!isDropdownOpen);
                setSearchQuery('');
              }}
              className="w-full flex items-center justify-between py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all text-left"
            >
              <span>{formData.instructions || 'Select Skill'}</span>
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
            </button>

            {isDropdownOpen && (
              <div className="absolute z-50 w-full mt-2 bg-white dark:bg-[#001736] border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 bg-gray-50/50 dark:bg-[#001020]/50">
                  <Search className="w-4 h-4 text-gray-400 shrink-0 ml-1" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 py-1"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="max-h-60 overflow-y-auto py-1">
                  {filteredDropdownOptions.length > 0 ? (
                    filteredDropdownOptions.map((opt) => {
                      const isSelected = formData.instructions === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, instructions: opt });
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left py-2 px-4 text-sm hover:bg-[#1a3884]/10 dark:hover:bg-[#1a3884]/20 transition-all flex items-center justify-between
                            ${isSelected ? 'bg-[#1a3884]/5 dark:bg-[#1a3884]/15 font-bold text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-700 dark:text-gray-300'}`}
                        >
                          <span>{opt}</span>
                          {isSelected && <CheckCircle className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />}
                        </button>
                      );
                    })
                  ) : (
                    <div className="py-3 px-4 text-xs text-gray-400 text-center">
                      No matches found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Description *</label>
            <textarea
              name="description" value={formData.description} onChange={handleBasicInfoChange} rows={3}
              placeholder="Describe the purpose and scope of this assessment..."
              className="w-full py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001020] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none"
            />
          </div>
        </div>

        {renderSettingsAndToggles()}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-4 p-1.5 bg-gray-50 dark:bg-[#001020] rounded-2xl border border-gray-200 dark:border-gray-800">
        <button
          type="button"
          onClick={() => {
            if (assessmentType === 'course') return;
            // 1. Save skill state
            setSkillFormData(formData);
            // 2. Load course state
            setFormData(courseFormData);
            setAssessmentType('course');
            setQuestionForm(emptyQuestion(courseFormData.assessmentEngineType || 'KNOW'));
          }}
          className={`flex-1 py-3 px-4 rounded-xl font-extrabold text-sm flex items-center justify-center gap-2.5 transition-all duration-200
            ${assessmentType === 'course'
              ? 'bg-[#1a3884] text-white shadow-lg shadow-[#1a3884]/20'
              : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
            }`}
        >
          <BookOpen className="w-4.5 h-4.5" />
          Course Assessment
        </button>
        <button
          type="button"
          onClick={() => {
            if (assessmentType === 'skill') return;
            // 1. Save course state
            setCourseFormData(formData);
            // 2. Load skill state
            setFormData(skillFormData);
            setAssessmentType('skill');
            setQuestionForm(emptyQuestion(skillFormData.assessmentEngineType || 'KNOW'));
          }}
          className={`flex-1 py-3 px-4 rounded-xl font-extrabold text-sm flex items-center justify-center gap-2.5 transition-all duration-200
            ${assessmentType === 'skill'
              ? 'bg-[#1a3884] text-white shadow-lg shadow-[#1a3884]/20'
              : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
            }`}
        >
          <Wrench className="w-4.5 h-4.5" />
          Skill Assessment
        </button>
      </div>

      {assessmentType === 'skill' ? renderSkillForm() : renderCourseForm()}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Dynamic Question Form (Type-Aware)
  // ─────────────────────────────────────────────────────────────────────────────
  const renderQuestionForm = () => {
    const t = formData.assessmentEngineType;
    const eng = selectedEngine;

    return (
      <div className="bg-gray-50 dark:bg-[#001020] p-5 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-4">
        {/* Form header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {eng && (
              <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${eng.badge}`}>
                {eng.label}
              </span>
            )}
            <h3 className="font-bold text-gray-800 dark:text-white text-sm">
              {editingIndex >= 0 ? 'Edit Question' : 'Add New Question'}
            </h3>
          </div>
          {editingIndex >= 0 && (
            <button onClick={resetQuestionForm} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Common fields: Q ID + Quotient + Difficulty */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Question ID *</label>
            <input name="questionId" value={questionForm.questionId} onChange={handleQuestionFormChange}
              className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
              placeholder="e.g. Q101" />
          </div>
          <div>
            <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Quotient *</label>
            <select name="quotient" value={questionForm.quotient} onChange={handleQuestionFormChange}
              className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all">
              {QUOTIENTS.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Difficulty *</label>
            <select name="difficultyLevel" value={questionForm.difficultyLevel} onChange={handleQuestionFormChange}
              className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all">
              {DIFFICULTY_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* Question Text */}
        <div>
          <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">
            {t === 'WRITE' ? 'Writing Prompt *' : t === 'JUDGE' ? 'Scenario / Situation *' : 'Question Text *'}
          </label>
          <textarea name="questionText" value={questionForm.questionText} onChange={handleQuestionFormChange} rows={3}
            placeholder={t === 'WRITE' ? 'Describe the writing task...' : t === 'JUDGE' ? 'Describe the ethical scenario or decision situation...' : 'Enter question...'}
            className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none" />
        </div>

        {/* ── KNOW: 4 MCQ Options + Correct Answer */}
        {t === 'KNOW' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {['A', 'B', 'C', 'D'].map(opt => (
                <div key={opt}>
                  <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Option {opt} *</label>
                  <input name={`option${opt}`} value={questionForm[`option${opt}`]} onChange={handleQuestionFormChange}
                    className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
                    placeholder={`Option ${opt}`} />
                </div>
              ))}
            </div>
            <div className="w-1/2">
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Correct Answer *</label>
              <select name="correctAnswer" value={questionForm.correctAnswer} onChange={handleQuestionFormChange}
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-yellow-50 dark:bg-amber-900/20 text-gray-900 dark:text-white focus:ring-4 focus:ring-amber-400/20 focus:border-amber-500 outline-none transition-all">
                {['A', 'B', 'C', 'D'].map(o => <option key={o} value={o}>Option {o}</option>)}
              </select>
            </div>
          </>
        )}

        {/* ── CODE: Language + Starter Code + Expected Output */}
        {t === 'CODE' && (
          <>
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Programming Language *</label>
              <select name="language" value={questionForm.language} onChange={handleQuestionFormChange}
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all">
                {CODE_LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Starter Code (Optional)</label>
              <textarea name="starterCode" value={questionForm.starterCode} onChange={handleQuestionFormChange} rows={4}
                placeholder={`# Write your ${questionForm.language} starter code here...`}
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white font-mono focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Expected Output *</label>
              <textarea name="expectedOutput" value={questionForm.expectedOutput} onChange={handleQuestionFormChange} rows={2}
                placeholder="Expected output that Judge0 will match against..."
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white font-mono focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none" />
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-700 dark:text-emerald-400 font-medium">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Code execution via Judge0 will be configured in Phase 2. Fields are stored for now.
            </div>
          </>
        )}

        {/* ── DATA: Expected Answer + Tolerance */}
        {t === 'DATA' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Expected Answer *</label>
              <input name="expectedAnswer" value={questionForm.expectedAnswer} onChange={handleQuestionFormChange}
                placeholder="e.g. 12.5 or Paris"
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Tolerance % (Numeric answers)</label>
              <input name="tolerance" value={questionForm.tolerance} onChange={handleQuestionFormChange} type="number" min="0" max="50"
                placeholder="5"
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all" />
            </div>
          </div>
        )}

        {/* ── WRITE: Rubric + Word Limit */}
        {t === 'WRITE' && (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Scoring Rubric *</label>
              <textarea name="rubric" value={questionForm.rubric} onChange={handleQuestionFormChange} rows={3}
                placeholder="e.g. Clarity (25pts), Structure (25pts), Professionalism (25pts), Conciseness (25pts)"
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none" />
            </div>
            <div className="w-1/2">
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Word Limit</label>
              <input name="wordLimit" value={questionForm.wordLimit} onChange={handleQuestionFormChange} type="number" min="0"
                placeholder="e.g. 250"
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all" />
            </div>
          </div>
        )}

        {/* ── TOOL: Tool Name + Instructions */}
        {t === 'TOOL' && (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Tool *</label>
              <select name="toolName" value={questionForm.toolName} onChange={handleQuestionFormChange}
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all">
                {TOOL_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Task Instructions</label>
              <textarea name="toolInstructions" value={questionForm.toolInstructions} onChange={handleQuestionFormChange} rows={3}
                placeholder="Describe exactly what the student needs to do in the tool..."
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all resize-none" />
            </div>
          </div>
        )}

        {/* ── JUDGE: 4 options (ranked) + Concordance Key */}
        {t === 'JUDGE' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {['A', 'B', 'C', 'D'].map(opt => (
                <div key={opt}>
                  <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Response {opt} *</label>
                  <input name={`option${opt}`} value={questionForm[`option${opt}`]} onChange={handleQuestionFormChange}
                    className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all"
                    placeholder={`Response ${opt} (ranked choice)`} />
                </div>
              ))}
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Concordance / Ranking Key *</label>
              <input name="concordanceKey" value={questionForm.concordanceKey} onChange={handleQuestionFormChange}
                placeholder="e.g. A>C>B>D (best to worst ranking)"
                className="w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-[#001736] text-gray-900 dark:text-white focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#1a3884] outline-none transition-all" />
              <p className="text-[10px] text-gray-400 mt-1">Use &gt; to indicate ranking order (A&gt;B means A is better than B)</p>
            </div>
          </>
        )}

        {/* Save / Cancel Buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          {editingIndex >= 0 && (
            <button onClick={resetQuestionForm} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">
              Cancel Edit
            </button>
          )}
          <button onClick={saveQuestion}
            className="px-5 py-2 bg-[#1a3884] dark:bg-[#1a3884] hover:bg-[#00152E] dark:hover:bg-[#4f7dff] text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-md shadow-[#1a3884]/20">
            <Plus className="w-4 h-4" />
            {editingIndex >= 0 ? 'Update Question' : 'Save Question'}
          </button>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Step 2 — Questions
  // ─────────────────────────────────────────────────────────────────────────────
  const renderStep2 = () => {
    const eng = selectedEngine;
    const Icon = eng?.icon;
    return (
      <div className="space-y-5">
        {/* Header + engine badge + actions */}
        <div className="bg-white dark:bg-[#001736] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3">
              {eng && (
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br ${eng.gradient} text-white shadow-md`}>
                  {Icon && <Icon className="w-5 h-5" />}
                </div>
              )}
              <div>
                <h2 className="text-base font-extrabold text-gray-900 dark:text-white">
                  Questions
                  <span className="ml-2 text-sm text-gray-400 dark:text-gray-500 font-normal">({formData.questions.length} added)</span>
                </h2>
                {eng && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${eng.badge}`}>{eng.label}</span>
                    <span className="text-[10px] text-gray-400">{eng.title}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={downloadSampleCSV}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-800 text-[#1a3884] dark:text-[#4f7dff] rounded-xl text-xs font-bold border border-gray-200 dark:border-gray-700 hover:border-[#1a3884] transition-colors">
                <Download className="w-3.5 h-3.5" /> Template
              </button>
              <label className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-xl text-xs font-bold border border-emerald-200 dark:border-emerald-800 cursor-pointer hover:bg-emerald-100 transition-colors">
                <Upload className="w-3.5 h-3.5" /> Bulk Import
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
              </label>
            </div>
          </div>

          {/* Dynamic Question Form */}
          {renderQuestionForm()}

          {/* Question List */}
          <div className="mt-5 space-y-2">
            {formData.questions.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">No questions added yet</p>
              </div>
            ) : (
              formData.questions.map((q, idx) => {
                const qEng = ENGINE_TYPES.find(e => e.id === q.engineType) || eng;
                const QIcon = qEng?.icon;
                return (
                  <div key={idx} className={`flex justify-between items-start p-3.5 bg-white dark:bg-[#001020] border rounded-2xl hover:border-[#1a3884]/30 dark:hover:border-[#4f7dff]/30 transition-colors ${editingIndex === idx ? 'border-[#1a3884] ring-2 ring-[#1a3884]/20' : 'border-gray-100 dark:border-gray-800'}`}>
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${qEng?.gradient} text-white shadow-sm`}>
                        {QIcon && <QIcon className="w-3.5 h-3.5" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap gap-1 items-center mb-1">
                          <span className="text-[10px] font-black text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 px-1.5 py-0.5 rounded-md">{q.questionId}</span>
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-md ${qEng?.badge}`}>{q.engineType}</span>
                          <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-1.5 py-0.5 rounded-md">{q.quotient}</span>
                          <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded-md">{q.difficultyLevel}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{q.questionText}</p>
                        {/* Type-specific summary */}
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                          {q.engineType === 'CODE' && `${q.language} · Expected: ${q.expectedOutput?.slice(0, 30)}${q.expectedOutput?.length > 30 ? '…' : ''}`}
                          {q.engineType === 'DATA' && `Answer: ${q.expectedAnswer} ± ${q.tolerance}%`}
                          {q.engineType === 'WRITE' && `Word limit: ${q.wordLimit || 'None'}`}
                          {q.engineType === 'TOOL' && q.toolName}
                          {q.engineType === 'JUDGE' && `Concordance: ${q.concordanceKey}`}
                          {q.engineType === 'KNOW' && `Correct: ${q.correctAnswer}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <button onClick={() => editQuestion(idx)} className="p-1.5 text-[#1a3884] dark:text-[#4f7dff] hover:bg-[#00152E]/5 dark:hover:bg-white/5 rounded-lg transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteQuestion(idx)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="flex justify-between">
          <button onClick={() => setCurrentStep(1)} className="px-5 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl font-medium transition-colors">
            ← Back
          </button>
          <button
            onClick={() => setCurrentStep(3)}
            disabled={formData.questions.length === 0}
            className="bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#4f7dff] text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-lg shadow-[#1a3884]/20 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Review Assessment <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Step 3 — Review & Save
  // ─────────────────────────────────────────────────────────────────────────────
  const renderStep3 = () => {
    const eng = selectedEngine;
    const Icon = eng?.icon;

    // Count by quotient
    const quotientBreakdown = QUOTIENTS.reduce((acc, q) => {
      const count = formData.questions.filter(x => x.quotient === q).length;
      if (count > 0) acc[q] = count;
      return acc;
    }, {});
    const diffBreakdown = DIFFICULTY_LEVELS.reduce((acc, d) => {
      acc[d] = formData.questions.filter(x => x.difficultyLevel === d).length;
      return acc;
    }, {});

    return (
      <div className="space-y-5">
        <div className="bg-white dark:bg-[#001736] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 space-y-6">
          {/* Header */}
          <div className="text-center pb-4 border-b border-gray-100 dark:border-gray-800">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${eng?.gradient} text-white flex items-center justify-center mx-auto mb-3 shadow-lg`}>
              {Icon && <Icon className="w-7 h-7" />}
            </div>
            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">{formData.assessmentName}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formData.description}</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              {eng && <span className={`text-xs font-black uppercase px-2.5 py-1 rounded-full ${eng.badge}`}>{eng.label} — {eng.title}</span>}
              <AIBadge level={eng?.ai || 'No'} />
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Questions', value: formData.questions.length, icon: FileText },
              { label: 'Duration', value: `${formData.duration} min`, icon: Play },
              { label: 'Passing Score', value: `${formData.passingScore}%`, icon: CheckCircle },
              { label: 'Max Attempts', value: formData.maxAttempts, icon: AlertCircle },
            ].map(s => (
              <div key={s.label} className="p-3 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-800 text-center">
                <p className="text-xl font-black text-[#1a3884] dark:text-[#4f7dff]">{s.value}</p>
                <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-800">
              <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">Quotient Breakdown</p>
              <div className="space-y-1.5">
                {Object.entries(quotientBreakdown).map(([q, count]) => (
                  <div key={q} className="flex items-center justify-between">
                    <span className="text-xs font-bold text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full">{q}</span>
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{count} q{count !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-800">
              <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">Difficulty Spread</p>
              <div className="space-y-2">
                {DIFFICULTY_LEVELS.map(d => {
                  const count = diffBreakdown[d] || 0;
                  const pct = formData.questions.length > 0 ? Math.round((count / formData.questions.length) * 100) : 0;
                  return (
                    <div key={d}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="font-bold text-gray-600 dark:text-gray-300">{d}</span>
                        <span className="text-gray-500 dark:text-gray-400">{count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${d === 'Easy' ? 'bg-green-500' : d === 'Medium' ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Question preview */}
          <div className="bg-gray-50 dark:bg-gray-900/30 p-4 rounded-xl border border-gray-100 dark:border-gray-800 max-h-52 overflow-y-auto">
            <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Questions Preview</p>
            <ul className="space-y-1.5">
              {formData.questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-gray-400 font-bold w-5 flex-shrink-0">{i + 1}.</span>
                  <span className="text-gray-700 dark:text-gray-300 flex-1 min-w-0 truncate">{q.questionText}</span>
                  <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{q.quotient} / {q.difficultyLevel}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
            <button onClick={() => setCurrentStep(2)} className="px-5 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl font-medium transition-colors">
              ← Back to Edit
            </button>
            <div className="flex gap-3">
              <button onClick={() => handleFinalSave('draft')} disabled={loading}
                className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-bold hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors">
                <Save className="w-4 h-4" /> Save as Draft
              </button>
              <button onClick={() => handleFinalSave('active')} disabled={loading}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-colors shadow-lg shadow-emerald-600/20">
                <CheckCircle className="w-4 h-4" />
                {loading ? 'Saving…' : 'Activate Assessment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 px-4 md:px-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/assessments')}
          className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 shadow-sm flex items-center justify-center">
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-xl font-extrabold text-[#00152E] dark:text-white">
            {isEditMode ? 'Edit Assessment' : 'Assessment Builder'}
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Step {currentStep} of 3
            {formData.assessmentEngineType && (
              <> · <span className="font-bold text-[#1a3884] dark:text-[#4f7dff]">{formData.assessmentEngineType}</span> engine</>
            )}
          </p>
        </div>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-2">
        {[
          { n: 1, label: 'Setup & Engine' },
          { n: 2, label: 'Questions' },
          { n: 3, label: 'Review' }
        ].map((s, i) => (
          <React.Fragment key={s.n}>
            {i > 0 && <div className={`flex-1 h-0.5 transition-colors duration-300 ${currentStep > s.n ? 'bg-[#1a3884]' : currentStep === s.n ? 'bg-[#1a3884]/40' : 'bg-gray-200 dark:bg-gray-700'}`} />}
            <div className={`flex items-center gap-1.5 flex-shrink-0`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${currentStep === s.n ? 'bg-[#1a3884] text-white ring-4 ring-[#1a3884]/20' : currentStep > s.n ? 'bg-emerald-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                {currentStep > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
              </div>
              <span className={`text-[10px] font-bold hidden md:block ${currentStep === s.n ? 'text-[#1a3884] dark:text-[#4f7dff]' : currentStep > s.n ? 'text-emerald-600' : 'text-gray-400'}`}>{s.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </div>
  );
};

// ─── Need Check icon ──────────────────────────────────────────────────────────
const Check = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export default CreateAssessment;
